import { createSlice } from '@reduxjs/toolkit';
import { isAlertCategory } from 'config/notifications.config';

/**
 * Notifications slice (D5a) — backs the two-surface Alert Hub:
 *   - NOTIFICATIONS (Bell): the personal inbox, read lifecycle, `unread` badge.
 *   - ALERTS (CircleAlert): unresolved operational issues, resolve lifecycle,
 *     `unresolved` badge. "read != resolved" — the two counts are independent.
 *
 * Backed by the D4/D-amend v5 API (`/v5/:orgID/notifications/inbox*`). Scoped by orgID.
 * Lists are keyed by SURFACE so the Inbox tab/bell and the Attention tab/alerts popover
 * page independently:
 *   state.Notifications.lists[surface][orgID] = { items, nextCursor, loading, exhausted }
 * where surface ∈ 'inbox' (state=all) | 'attention' (state=unresolved).
 *
 * Mutations update optimistically here; the saga calls the API and re-syncs counts on
 * failure (the server counts are the source of truth for the badges).
 */

// An ALERT row is one whose category routes to the Alerts surface — the single
// source (ALERT_CATEGORIES / isAlertCategory) lives in config/notifications.config.
const isAlertRow = (row) => row && isAlertCategory(row.category);

const initialState = {
	// lists[surface][orgID] = { items: [], nextCursor: null, loading: false, exhausted: false }
	lists: { inbox: {}, attention: {} },
	// counts[orgID] = { unread: number, unresolved: number }
	counts: {},
	// filters[orgID] = { category, severity, siteID } — full-page/tab facets
	filters: {},
	// preferences[orgID] = { matrix: [event×channel rows], masters: {ch: bool}, loading, saving, loaded } (D5b)
	preferences: {},
	// adminPreferences[scopeKey] = { events, modules, loading, saving, loaded } — the org/site admin
	// defaults editor (B2/P2b). scopeKey = `${orgID}:org` or `${orgID}:site:${siteID}`.
	adminPreferences: {},
	error: null
};

const emptyList = () => ({ items: [], nextCursor: null, loading: false, exhausted: false });

const ensureList = (state, surface, orgID) => {
	if (!state.lists[surface]) state.lists[surface] = {};
	if (!state.lists[surface][orgID]) state.lists[surface][orgID] = emptyList();
	return state.lists[surface][orgID];
};

const ensureCounts = (state, orgID) => {
	if (!state.counts[orgID]) state.counts[orgID] = { unread: 0, unresolved: 0 };
	return state.counts[orgID];
};

const ensurePreferences = (state, orgID) => {
	if (!state.preferences[orgID])
		state.preferences[orgID] = {
			matrix: [],
			masters: {},
			modules: { enabled: [], layout: 'rail' },
			loading: false,
			saving: false,
			loaded: false
		};
	return state.preferences[orgID];
};

// scopeKey groups an org's admin default editor by scope: `<orgID>:org` or `<orgID>:site:<siteID>`.
export const adminScopeKey = ({ orgID, scope, siteID }) =>
	scope === 'site' ? `${orgID}:site:${siteID}` : `${orgID}:org`;

const ensureAdminPrefs = (state, payload) => {
	const key = adminScopeKey(payload);
	if (!state.adminPreferences[key])
		state.adminPreferences[key] = {
			events: [],
			modules: { enabled: [], layout: 'rail' },
			channelMasters: {},
			loading: false,
			saving: false,
			loaded: false
		};
	return state.adminPreferences[key];
};

const dec = (n) => Math.max(0, (n || 0) - 1);

// The FIRST list copy of a notification (its canonical state) + the org it belongs to.
// An ALERT row lives in BOTH the inbox and attention buckets, so counts must be adjusted
// from this single canonical read — never once per bucket (that double-counts the badge).
const findRow = (state, notificationID) => {
	for (const surface of Object.keys(state.lists)) {
		for (const orgID of Object.keys(state.lists[surface])) {
			const row = state.lists[surface][orgID].items.find((r) => r.notificationID === notificationID);
			if (row) return { row, orgID };
		}
	}
	return {};
};

// Apply a per-item mutation to EVERY bucket copy of the id (no count side effects here —
// the caller adjusts counts once from findRow). fn receives (bucket, idx, surface).
const mutateAllCopies = (state, notificationID, fn) => {
	for (const surface of Object.keys(state.lists)) {
		for (const orgID of Object.keys(state.lists[surface])) {
			const bucket = state.lists[surface][orgID];
			const idx = bucket.items.findIndex((r) => r.notificationID === notificationID);
			if (idx !== -1) fn(bucket, idx, surface);
		}
	}
};

const notificationsSlice = createSlice({
	name: 'notifications',
	initialState,
	reducers: {
		// ── List load (saga listens; these reducers manage the bucket) ──────────
		loadList: () => {}, // trigger: { surface }
		loadMoreList: () => {}, // trigger: { surface }
		loadListStart(state, action) {
			const { surface, orgID } = action.payload;
			ensureList(state, surface, orgID).loading = true;
			state.error = null;
		},
		loadListSuccess(state, action) {
			const { surface, orgID, items, nextCursor, append = false } = action.payload;
			const bucket = ensureList(state, surface, orgID);
			bucket.items = append ? [...bucket.items, ...items] : items;
			bucket.nextCursor = nextCursor ?? null;
			bucket.exhausted = nextCursor == null;
			bucket.loading = false;
		},
		loadListError(state, action) {
			const { surface, orgID, error } = action.payload;
			if (state.lists[surface]?.[orgID]) state.lists[surface][orgID].loading = false;
			state.error = error;
		},
		setFilters(state, action) {
			const { orgID, filters } = action.payload;
			state.filters[orgID] = { ...(state.filters[orgID] || {}), ...filters };
		},

		// ── Counts (Bell + Alerts badges) ───────────────────────────────────────
		loadCounts: () => {}, // trigger
		setCounts(state, action) {
			const { orgID, unread, unresolved } = action.payload;
			const c = ensureCounts(state, orgID);
			if (unread != null) c.unread = unread;
			if (unresolved != null) c.unresolved = unresolved;
		},

		// ── Preferences matrix (D5b — event × channel) ─────────────────────────
		loadPreferences: () => {}, // trigger
		loadPreferencesStart(state, action) {
			ensurePreferences(state, action.payload.orgID).loading = true;
			state.error = null;
		},
		loadPreferencesSuccess(state, action) {
			const { orgID, matrix, masters, modules } = action.payload;
			const p = ensurePreferences(state, orgID);
			p.matrix = matrix || [];
			p.masters = masters || {};
			if (modules) p.modules = modules;
			p.loading = false;
			p.loaded = true;
		},
		loadPreferencesError(state, action) {
			const { orgID, error } = action.payload;
			ensurePreferences(state, orgID).loading = false;
			state.error = error;
		},
		// Optimistic toggle: apply the change to the local matrix/masters + mark saving;
		// the saga PATCHes and replaces with the server view (or refetches on error).
		updatePreferences(state, action) {
			const { orgID, subscriptions, channels } = action.payload;
			const p = ensurePreferences(state, orgID);
			p.saving = true;
			if (Array.isArray(channels)) {
				channels.forEach(({ channel, enabled }) => {
					p.masters[channel] = enabled;
				});
			}
			if (Array.isArray(subscriptions)) {
				subscriptions.forEach(({ eventType, channel, enabled }) => {
					const ev = p.matrix.find((e) => e.eventType === eventType);
					// A manual per-channel toggle is a personal override; the row's level becomes custom
					// until the server view (which recomputes it) replaces this optimistic state.
					if (ev && ev.channels[channel] && !ev.channels[channel].locked) {
						ev.channels[channel] = { enabled, source: 'override', origin: 'personal', locked: false };
						ev.level = 'custom';
					}
				});
			}
		},
		// Optimistic delivery-level preset (B2): mark saving; the saga PATCHes /preferences/level
		// and the server view (with recomputed channels + level) replaces it via ...Success.
		setEventLevel(state, action) {
			const { orgID, eventType, level } = action.payload;
			const p = ensurePreferences(state, orgID);
			p.saving = true;
			const ev = p.matrix.find((e) => e.eventType === eventType);
			if (ev) ev.level = level;
		},
		updatePreferencesSuccess(state, action) {
			const { orgID, matrix, masters, modules } = action.payload;
			const p = ensurePreferences(state, orgID);
			if (matrix) p.matrix = matrix;
			if (masters) p.masters = masters;
			if (modules) p.modules = modules;
			p.saving = false;
		},
		updatePreferencesError(state, action) {
			ensurePreferences(state, action.payload.orgID).saving = false;
		},
		// Reset-to-defaults (saga POSTs /preferences/reset, then replaces via updatePreferencesSuccess).
		// Payload { orgID, eventType? } — eventType scopes it to one event; absent = full reset.
		resetPreferences(state, action) {
			ensurePreferences(state, action.payload.orgID).saving = true;
		},

		// ── Admin org/site defaults editor (B2/P2b) ─────────────────────────────
		loadAdminPreferences: () => {}, // trigger { orgID, scope, siteID? }
		loadAdminPreferencesStart(state, action) {
			ensureAdminPrefs(state, action.payload).loading = true;
			state.error = null;
		},
		loadAdminPreferencesSuccess(state, action) {
			const a = ensureAdminPrefs(state, action.payload);
			a.events = action.payload.events || [];
			if (action.payload.modules) a.modules = action.payload.modules;
			if (action.payload.channelMasters) a.channelMasters = action.payload.channelMasters;
			a.loading = false;
			a.loaded = true;
		},
		loadAdminPreferencesError(state, action) {
			ensureAdminPrefs(state, action.payload).loading = false;
			state.error = action.payload.error;
		},
		// Optimistic admin cell edit ({ orgID, scope, siteID?, cells: [{eventType, channel, enabled, mutable}] }).
		updateAdminPreferences(state, action) {
			const a = ensureAdminPrefs(state, action.payload);
			a.saving = true;
			(action.payload.cells || []).forEach(({ eventType, channel, enabled, mutable }) => {
				const ev = a.events.find((e) => e.eventType === eventType);
				if (ev && ev.channels[channel])
					ev.channels[channel] = { enabled, mutable: mutable !== false, set: true };
			});
			// Optimistic channel-master toggle (org/site kill switch).
			(action.payload.channelMasters || []).forEach(({ channel, enabled }) => {
				if (!a.channelMasters) a.channelMasters = {};
				a.channelMasters[channel] = { enabled, set: true };
			});
		},
		updateAdminPreferencesSuccess(state, action) {
			const a = ensureAdminPrefs(state, action.payload);
			if (action.payload.events) a.events = action.payload.events;
			if (action.payload.modules) a.modules = action.payload.modules;
			if (action.payload.channelMasters) a.channelMasters = action.payload.channelMasters;
			a.saving = false;
		},
		updateAdminPreferencesError(state, action) {
			ensureAdminPrefs(state, action.payload).saving = false;
		},
		resetAdminPreferences(state, action) {
			ensureAdminPrefs(state, action.payload).saving = true;
		},

		// ── Live socket arrival (NOTIFICATION_NEW) ──────────────────────────────
		notificationReceived(state, action) {
			const row = action.payload;
			if (!row || row.orgID == null) return;
			const orgID = row.orgID;
			const alert = isAlertRow(row);
			// Dedupe against the canonical inbox bucket: socket.io can redeliver an event
			// (e.g. on reconnect), and the counts must only move for a GENUINELY new row —
			// otherwise a duplicate NOTIFICATION_NEW inflates the badge.
			const inbox = ensureList(state, 'inbox', orgID);
			if (inbox.items.some((r) => r.notificationID === row.notificationID)) return;

			inbox.items.unshift(row);
			if (alert) {
				const attention = ensureList(state, 'attention', orgID);
				if (!attention.items.some((r) => r.notificationID === row.notificationID)) attention.items.unshift(row);
			}
			const c = ensureCounts(state, orgID);
			if (row.readDate == null) c.unread += 1;
			if (alert && row.resolvedDate == null) c.unresolved += 1;
		},

		// ── Optimistic mutations (saga does the API + reconcile on error) ───────
		markRead(state, action) {
			const { notificationID } = action.payload;
			const { row, orgID } = findRow(state, notificationID);
			if (!row || row.readDate != null) return; // absent or already read → no-op (idempotent)
			const now = new Date().toISOString();
			mutateAllCopies(state, notificationID, (bucket, idx) => (bucket.items[idx].readDate = now));
			ensureCounts(state, orgID).unread = dec(state.counts[orgID].unread);
		},
		markAllRead(state, action) {
			const { orgID } = action.payload;
			for (const surface of Object.keys(state.lists)) {
				const bucket = state.lists[surface]?.[orgID];
				if (bucket) bucket.items.forEach((r) => (r.readDate = r.readDate || new Date().toISOString()));
			}
			ensureCounts(state, orgID).unread = 0;
		},
		markSeen: () => {}, // trigger: { surface?, ids? } — seen is ambient, no count/optimistic change
		dismiss(state, action) {
			const { notificationID } = action.payload;
			const { row, orgID } = findRow(state, notificationID);
			if (!row) return;
			// Read the canonical state ONCE, then remove every copy + adjust counts once.
			const wasUnread = row.readDate == null;
			const wasActiveAlert = isAlertRow(row) && row.resolvedDate == null && !row.snoozedUntil;
			mutateAllCopies(state, notificationID, (bucket, idx) => bucket.items.splice(idx, 1));
			const c = ensureCounts(state, orgID);
			if (wasUnread) c.unread = dec(c.unread);
			if (wasActiveAlert) c.unresolved = dec(c.unresolved);
		},
		resolve(state, action) {
			const { notificationID } = action.payload;
			const { row, orgID } = findRow(state, notificationID);
			if (!row) return;
			const wasActive = row.resolvedDate == null && !row.snoozedUntil;
			const now = new Date().toISOString();
			mutateAllCopies(state, notificationID, (bucket, idx, surface) => {
				bucket.items[idx].resolvedDate = bucket.items[idx].resolvedDate || now;
				// Drop from the attention list; keep in the inbox as read history.
				if (surface === 'attention') bucket.items.splice(idx, 1);
			});
			if (wasActive) ensureCounts(state, orgID).unresolved = dec(state.counts[orgID].unresolved);
		},
		snooze(state, action) {
			const { notificationID, until } = action.payload;
			const { row, orgID } = findRow(state, notificationID);
			if (!row) return;
			const wasActive = row.resolvedDate == null && !row.snoozedUntil;
			mutateAllCopies(state, notificationID, (bucket, idx, surface) => {
				bucket.items[idx].snoozedUntil = until;
				if (surface === 'attention') bucket.items.splice(idx, 1);
			});
			if (wasActive) ensureCounts(state, orgID).unresolved = dec(state.counts[orgID].unresolved);
		}
	},
	extraReducers: (builder) => {
		// Clear everything on logout (house convention — matches the other slices).
		builder.addCase('auth/logout', () => initialState);
	}
});

export const notificationsActions = notificationsSlice.actions;
export default notificationsSlice.reducer;
